import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  cancelGrading,
  deleteAllGradingRuns,
  deleteGradingRun,
  deleteRubric,
  fetchClassStudents,
  fetchExamDetail,
  fetchExamGrades,
  fetchExamAnalysis,
  pollGrading,
  regradeQuestions,
  startGrading,
  startExamDGrading,
  uploadExamSubmission,
  uploadExamDSubmission,
} from "../api/client";
import type {
  SessionAnalysis,
  SessionDetail,
  SessionGrades,
  StudentSummary,
} from "../api/types";
import { useOrgContext } from "../state/OrgContext";
import {
  Settings,
  Play,
  Lightbulb,
  TrendingUp,
  TrendingDown,
  Users,
  Download,
  CheckCircle2,
  Copy,
  Check,
  X,
  ClipboardList,
  Loader2,
  Sparkles,
  Upload,

} from "lucide-react";
import { DifficultyIndicator } from "../components/DifficultyIndicator";
import { ScoreRing } from "../components/ScoreRing";
import { useHeaderTitle } from "../components/HeaderTitleContext";
import type { ExamDifficulty } from "../api/types";
import ExamDSideBySide from "./ExamDSideBySide";

const mapDifficultyLevel = (
  difficulty?: ExamDifficulty
): "easy" | "moderate" | "hard" | "very_hard" => {
  if (!difficulty) return "moderate";
  switch (difficulty.category) {
    case "Easy":
      return "easy";
    case "Hard":
      return "hard";
    case "Very Hard":
      return "very_hard";
    case "Moderate":
    default:
      return "moderate";
  }
};

type Status = "idle" | "loading" | "error";
type StudentFilter = "all" | "graded" | "pending";
type MainTab = "analysis" | "students" | "export";

const mapStudentStatus = (
  raw?: string | null
): "graded" | "pending" | "processing" | "error" => {
  if (!raw) return "pending";
  if (["processing", "running"].includes(raw)) return "processing";
  if (["failed", "error"].includes(raw)) return "error";
  if (["completed", "graded", "ready"].includes(raw)) return "graded";
  return "pending";
};

const progressColor = (pct: number) => {
  if (pct >= 80) return "success";
  if (pct >= 65) return "copper";
  if (pct >= 50) return "warning";
  return "danger";
};

const SessionDetailPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeRole } = useOrgContext();
  const { setTitle: setHeaderTitle } = useHeaderTitle();
  const { t } = useTranslation('pages');

  // Detect grading mode from URL path
  const gradingMode: "mod_ab" | "mod_c" | "mod_d" = location.pathname.startsWith("/exams-c/")
    ? "mod_c"
    : location.pathname.startsWith("/exams-d/")
    ? "mod_d"
    : "mod_ab";

  const setupPath = gradingMode === "mod_c"
    ? `/exams-c/${sessionId}/setup`
    : `/exams/${sessionId}/edit`;

  const editQuestionsPath = `/exams/${sessionId}/edit?tab=questions`;

  const editRubricPath = `/exams/${sessionId}/edit?tab=rubric`;
  const editPapersPath = `/exams/${sessionId}/edit?tab=papers`;

  const [status, setStatus] = useState<Status>("loading");
  const [detail, setDetail] = useState<SessionDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [gradingState, setGradingState] = useState<{
    gradingId?: number;
    progress?: number;
    papersGraded?: number;
    totalPapers?: number;
    status?: string;
    currentPaperName?: string;
  }>({});
  const [strictness, setStrictness] = useState("moderate");
  const [feedbackLanguage, setFeedbackLanguage] = useState("English");
  const [classStudents, setClassStudents] = useState<StudentSummary[]>([]);
  const [selectedPaperId, setSelectedPaperId] = useState<number | null>(null);
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(
    null
  );
  const [activeFilter, setActiveFilter] = useState<StudentFilter>("all");
  const [activeTab, setActiveTab] = useState<MainTab>("analysis");
  const [grades, setGrades] = useState<SessionGrades>([]);
  const [gradesStatus, setGradesStatus] = useState<Status>("loading");
  const [overallFeedbackDraft, setOverallFeedbackDraft] = useState("");
  const [analysisStatus, setAnalysisStatus] = useState<Status>("loading");
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [classAnalysis, setClassAnalysis] = useState<SessionAnalysis | null>(
    null
  );
  const [editingCell, setEditingCell] = useState<{
    questionNumber: number;
    field: "feedback";
  } | null>(null);
  const [editValue, setEditValue] = useState("");
  const [gradingLogs, setGradingLogs] = useState<string[]>([]);
  const [questionsApproved, setQuestionsApproved] = useState<boolean>(false);
  const [regradeSelected, setRegradeSelected] = useState<Set<string>>(new Set());
  const [regradeLoading, setRegradeLoading] = useState(false);
  const [rawJsonModal, setRawJsonModal] = useState<{ data: unknown; title: string } | null>(null);
  const logsEndRef = useRef<HTMLDivElement>(null);

  const studentsWithoutSubmission = useMemo(() => {
    if (!detail) return classStudents;
    const submittedIds = new Set(
      detail.papers
        .map((p) => p.student_id)
        .filter((id): id is number => Boolean(id))
    );
    return classStudents.filter((s) => !submittedIds.has(s.id));
  }, [classStudents, detail]);

  // --- Data loading ---

  const load = () => {
    if (!sessionId) return;
    setStatus("loading");
    fetchExamDetail(sessionId)
      .then((data) => {
        setDetail(data);
        const latestLanguage =
          data.latest_run?.feedback_language ||
          data.feedback_language ||
          data.runs?.[0]?.feedback_language ||
          "English";
        setFeedbackLanguage(latestLanguage);
        setStatus("idle");

        if (data.class_id) {
          fetchClassStudents(data.class_id)
            .then((rows) => setClassStudents(rows))
            .catch(() => setClassStudents([]));
        } else {
          setClassStudents([]);
          setSelectedStudentId(null);
        }

        if (data.papers.length > 0) {
          setSelectedPaperId((prev) => prev ?? data.papers[0].id);
        } else {
          setSelectedPaperId(null);
        }
      })
      .catch((err) => {
        setError(err.message || t('sessionDetail.loadError'));
        setStatus("error");
      });
  };

  useEffect(() => {
    load();
  }, [sessionId]);

  useEffect(() => {
    setHeaderTitle(detail?.session_name ?? null);
  }, [detail?.session_name, setHeaderTitle]);

  useEffect(() => {
    if (!detail || !sessionId) return;
    const latestRunId =
      detail.latest_run?.id || detail.runs?.[0]?.id || null;
    setGradesStatus("loading");
    fetchExamGrades(sessionId, latestRunId || undefined)
      .then((data) => setGrades(data))
      .catch(() => setGrades([]))
      .finally(() => setGradesStatus("idle"));
  }, [detail, sessionId]);

  useEffect(() => {
    if (!detail || !sessionId) return;
    const latestRunId =
      detail.latest_run?.id || detail.runs?.[0]?.id || null;
    
    // Only fetch analysis if there are grading runs
    if (!latestRunId) {
      setClassAnalysis(null);
      setAnalysisStatus("idle");
      return;
    }
    
    setAnalysisStatus("loading");
    setAnalysisError(null);
    fetchExamAnalysis(sessionId, latestRunId)
      .then((data) => {
        setClassAnalysis(data);
        setAnalysisStatus("idle");
      })
      .catch((err: any) => {
        setAnalysisError(
          err?.response?.data?.error || t('sessionDetail.analysisLoadError')
        );
        setClassAnalysis(null);
        setAnalysisStatus("error");
      });
  }, [detail, sessionId]);

  useEffect(() => {
    if (!studentsWithoutSubmission.length) {
      setSelectedStudentId(null);
      return;
    }
    if (
      !selectedStudentId ||
      !studentsWithoutSubmission.find((s) => s.id === selectedStudentId)
    ) {
      setSelectedStudentId(studentsWithoutSubmission[0].id);
    }
  }, [studentsWithoutSubmission, selectedStudentId]);

  useEffect(() => {
    if (detail) {
      setQuestionsApproved(detail.questions_approved ?? false);
    }
  }, [detail?.questions_approved]);

  // Auto-scroll logs
  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [gradingLogs]);

  // --- Legacy file operations (preserved) ---

  const uploadRubric = async (file: File) => {
    if (!sessionId) return;
    const fd = new FormData();
    fd.append("rubric_file", file);
    setUploading(true);
    try {
      const res = await fetch(`/upload_rubric/${sessionId}`, {
        method: "POST",
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error === "insufficient_credits" ? t('common.insufficientCredits') : (data.error || t('sessionDetail.rubricLoadError')));
      }
      load();
    } catch (err) {
      alert(err instanceof Error ? err.message : t('sessionDetail.rubricLoadError'));
    } finally {
      setUploading(false);
    }
  };

  const removeRubric = async () => {
    if (!sessionId) return;
    if (!confirm(t('sessionDetail.rubricRemoveConfirm'))) return;
    await deleteRubric(Number(sessionId));
    load();
  };

  const uploadQuestionPaper = async (file: File) => {
    if (!sessionId) return;
    const fd = new FormData();
    fd.append("question_paper", file);
    setUploading(true);
    try {
      const res = await fetch(`/upload_question_paper/${sessionId}`, {
        method: "POST",
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error === "insufficient_credits" ? t('common.insufficientCredits') : (data.error || t('sessionDetail.questionPaperLoadError')));
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : t('sessionDetail.questionPaperLoadError'));
    } finally {
      setUploading(false);
    }
    load();
  };

  const clearQuestions = async () => {
    if (!sessionId) return;
    await fetch(`/clear_questions/${sessionId}`, { method: "POST" });
    load();
  };

  const uploadForSelectedStudent = async (files: FileList | null) => {
    if (!files || !sessionId || !selectedStudentId) return;
    setUploading(true);
    try {
      if (gradingMode === "mod_d") {
        await uploadExamDSubmission(Number(sessionId), selectedStudentId, Array.from(files));
      } else if (files.length === 1) {
        await uploadExamSubmission(Number(sessionId), selectedStudentId, files[0]);
      } else {
        await uploadStudentBundle(files, selectedStudentId);
      }
      load();
    } catch (err) {
      alert(
      err instanceof Error ? err.message : t('sessionDetail.studentPaperLoadError')
    );
    } finally {
      setUploading(false);
    }
  };

  const uploadStudentBundle = async (
    files: FileList | null,
    forcedStudentId?: number
  ) => {
    if (!files || !sessionId) return;
    const studentId = forcedStudentId || selectedStudentId || undefined;
    if (!studentId) {
      alert(t('sessionDetail.selectStudentFirst'));
      return;
    }

    const fd = new FormData();
    fd.append("student_id", String(studentId));
    Array.from(files).forEach((file) => fd.append("files[]", file));
    const shouldHandleUploading = !forcedStudentId;
    if (shouldHandleUploading) setUploading(true);
    const res = await fetch(`/upload_student_pages/${sessionId}`, {
      method: "POST",
      body: fd,
    });
    if (shouldHandleUploading) setUploading(false);
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      alert(data.error || t('sessionDetail.studentPagesLoadError'));
      return;
    }
    load();
  };

  const deletePaper = async (paperId: number) => {
    if (!confirm(t('sessionDetail.deletePaperConfirm')))
      return;
    await fetch(`/delete_paper/${paperId}`, { method: "POST" });
    load();
  };

  const deleteRun = async (runId: number) => {
    if (!sessionId) return;
    if (!confirm(t('sessionDetail.deleteRunConfirm', { runId })))
      return;
    await deleteGradingRun(Number(sessionId), runId);
    load();
  };

  const deleteAllRuns = async () => {
    if (!sessionId) return;
    if (!confirm(t('sessionDetail.deleteAllRunsConfirm')))
      return;
    await deleteAllGradingRuns(Number(sessionId));
    load();
  };

  // --- Approve gate ---

  // --- Grading flow ---

  const startGradingFlow = async () => {
    if (!sessionId) return;

    // Soft gate: warn if questions exist but not approved
    if (detail?.questions && detail.questions.length > 0 && !questionsApproved) {
      const confirmed = window.confirm(
        t('sessionDetail.questionsNotApprovedConfirm')
      );
      if (!confirmed) return;
    }

    try {
      const started =
        gradingMode === "mod_d"
          ? await startExamDGrading(
              Number(sessionId),
              strictness,
              detail?.feedback_language || feedbackLanguage
            )
          : await startGrading(Number(sessionId), strictness, feedbackLanguage);
      setGradingState({
        gradingId: started.grading_id,
        totalPapers: started.total_papers,
        status: "running",
        progress: 0,
      });
      setGradingLogs([t('sessionDetail.gradingStartedLog')]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      if (msg === "insufficient_credits" || (err as any)?.response?.status === 402) {
        alert(t('common.insufficientCredits'));
      } else {
        alert(msg || t('sessionDetail.gradingStartError'));
      }
    }
  };

  useEffect(() => {
    if (!gradingState.gradingId) return;
    const id = gradingState.gradingId;
    // Guard: multiple in-flight fetch calls can each receive "completed",
    // so use a closure flag to ensure completion logs are appended only once.
    let completionHandled = false;
    const interval = setInterval(async () => {
      try {
        const data = await pollGrading(id);
        const paperName = data.current_paper?.student_name;
        setGradingState((prev) => ({
          ...prev,
          progress: data.progress,
          papersGraded: data.papers_graded,
          totalPapers: data.total_papers,
          status: data.status,
          currentPaperName: paperName,
        }));

        if (paperName) {
          setGradingLogs((prev) => {
            const line = t('sessionDetail.gradingPaperLog', { paperName });
            if (prev[prev.length - 1] !== line) return [...prev, line];
            return prev;
          });
        }

        if (data.status === "completed" || data.status === "failed" || data.status === "insufficient_credits") {
          clearInterval(interval);
          if (data.status === "completed" && !completionHandled) {
            completionHandled = true;
            setGradingLogs((prev) => [
              ...prev,
              t('sessionDetail.allPapersGradedLog'),
              t('sessionDetail.completedLog'),
            ]);
          }
          load();
          if (data.status === "insufficient_credits") {
            alert(t('sessionDetail.gradingStoppedCredits'));
          } else if (data.status === "failed" && data.error) {
            alert(data.error);
          }
        }
      } catch (err) {
        clearInterval(interval);
        setGradingState((prev) => ({ ...prev, status: "failed" }));
        alert(
          err instanceof Error ? err.message : t('sessionDetail.gradingFailed')
        );
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [gradingState.gradingId]);

  const cancelGradingFlow = async () => {
    if (!gradingState.gradingId) return;
    await cancelGrading(gradingState.gradingId);
    setGradingState({});
    setGradingLogs([]);
  };

  // --- Editing ---

  const startEdit = (
    questionNumber: number,
    field: "feedback",
    currentValue: string
  ) => {
    setEditingCell({ questionNumber, field });
    setEditValue(currentValue);
  };

  const saveEdit = () => {
    setEditingCell(null);
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue("");
  };

  const handleRegrade = async () => {
    if (!selectedPaperId || regradeSelected.size === 0) return;
    setRegradeLoading(true);
    try {
      const result = await regradeQuestions(
        selectedPaperId,
        Array.from(regradeSelected)
      );
      setGrades((prev) =>
        prev.map((entry) => {
          if (entry.paper_id !== selectedPaperId) return entry;
          const updatedMap = new Map(
            result.updated.map((u) => [u.question_number_display, u])
          );
          return {
            ...entry,
            paper_grade: entry.paper_grade
              ? {
                  ...entry.paper_grade,
                  total_score: result.paper_grade_total_score,
                  has_low_confidence: result.has_low_confidence,
                }
              : entry.paper_grade,
            question_grades: entry.question_grades.map((q) => {
              const display = q.question_display || String(q.question_number);
              const updated = updatedMap.get(display);
              return updated
                ? {
                    ...q,
                    score: updated.score,
                    max_score: updated.max_score,
                    feedback: updated.feedback,
                    question_number_matched: updated.question_number_matched,
                  }
                : q;
            }),
          };
        })
      );
      setRegradeSelected(new Set());
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('sessionDetail.regradeError');
      alert(message);
    } finally {
      setRegradeLoading(false);
    }
  };

  // --- Derived state ---

  const latestRun = detail?.latest_run || detail?.runs?.[0] || null;
  const rubricFileId = detail?.rubric_file_id || null;
  const hasRubric = Boolean(
    rubricFileId && rubricFileId !== "null" && rubricFileId !== "undefined"
  );
  const missingUploads = classStudents.length
    ? studentsWithoutSubmission.length
    : 0;
  // TEMP: disabled for testing — allow grading with any number of papers
  const tooFewPapers = false;
  // const tooFewPapers =
  //   classStudents.length > 0 &&
  //   (detail?.papers.length ?? 0) < classStudents.length / 2;

  const studentRows = useMemo(() => {
    if (!detail) return [];
    return detail.papers.map((paper) => {
      const gradeEntry = grades.find((g) => g.paper_id === paper.id);
      const gradeStatus = mapStudentStatus(gradeEntry?.paper_grade?.status);
      const score = gradeEntry?.paper_grade?.total_score ?? null;
      const maxScore = detail.max_points ?? null;
      return {
        paper,
        gradeEntry,
        status: gradeStatus,
        score,
        maxScore,
        lastRun:
          gradeEntry?.paper_grade?.graded_at ||
          latestRun?.created_at ||
          null,
      };
    });
  }, [detail, grades, latestRun]);

  const filteredStudents = useMemo(() => {
    if (activeFilter === "graded")
      return studentRows.filter((s) => s.status === "graded");
    if (activeFilter === "pending")
      return studentRows.filter(
        (s) => s.status === "pending" || s.status === "processing"
      );
    return studentRows;
  }, [studentRows, activeFilter]);

  const selectedRow = useMemo(() => {
    if (!selectedPaperId && filteredStudents.length > 0)
      return filteredStudents[0];
    return (
      filteredStudents.find((s) => s.paper.id === selectedPaperId) ||
      studentRows.find((s) => s.paper.id === selectedPaperId) ||
      null
    );
  }, [filteredStudents, selectedPaperId, studentRows]);

  useEffect(() => {
    if (!selectedPaperId && studentRows.length > 0) {
      setSelectedPaperId(studentRows[0].paper.id);
    }
  }, [selectedPaperId, studentRows]);

  useEffect(() => {
    const nextFeedback =
      selectedRow?.gradeEntry?.paper_grade?.overall_feedback || "";
    setOverallFeedbackDraft(nextFeedback);
  }, [selectedRow?.gradeEntry?.paper_grade?.overall_feedback]);

  const hasGrades = grades.some(
    (g) => g.paper_grade?.total_score != null
  );

  const classAverage = classAnalysis
    ? classAnalysis.class_average ||
      (() => {
        const averageScore = classAnalysis.question_stats.reduce(
          (sum, q) => sum + q.average_score,
          0
        );
        const maxScore = classAnalysis.question_stats.reduce(
          (sum, q) => sum + q.max_points,
          0
        );
        const successPercent =
          maxScore > 0 ? (averageScore / maxScore) * 100 : 0;
        return {
          average_score: Math.round(averageScore * 100) / 100,
          max_score: maxScore,
          success_percent: Math.round(successPercent * 10) / 10,
        };
      })()
    : null;

  const gradingStatus: "idle" | "running" | "completed" | "failed" = (() => {
    if (!gradingState.status && !gradingState.gradingId) return "idle";
    if (gradingState.status === "failed") return "failed";
    if (gradingState.status === "completed") return "completed";
    return "running";
  })();

  const progressPct = gradingState.totalPapers
    ? Math.round(
        ((gradingState.papersGraded || 0) / gradingState.totalPapers) * 100
      )
    : gradingState.progress || 0;

  // --- Loading / error states ---

  if (status === "loading") {
    return (
      <div className="animate-fade-in">
        <p className="text-muted">{t('sessionDetail.loading')}</p>
      </div>
    );
  }

  if (status === "error" || !detail) {
    return (
      <div className="animate-fade-in">
        <div
          style={{
            padding: "12px 16px",
            borderRadius: "var(--radius-md)",
            background: "var(--color-danger-bg)",
            color: "var(--color-danger)",
          }}
        >
          {error || t('sessionDetail.loadErrorState')}
        </div>
      </div>
    );
  }

  // --- Grading overlay ---

  const renderGradingOverlay = () => (
    <div className="grading-overlay animate-fade-in">
      <div className="grading-overlay-header">
        <div className="grading-overlay-title">
          {gradingStatus === "completed" ? (
            <>
              <CheckCircle2
                size={18}
                style={{ color: "var(--color-success)" }}
              />
              {t('sessionDetail.gradingDoneHeader')}
            </>
          ) : (
            <>
              <Loader2 size={18} className="spin" />
              {t('sessionDetail.gradingInProgressHeader')}
            </>
          )}
        </div>
        <span className="grading-overlay-count">
          {gradingState.papersGraded ?? 0} / {gradingState.totalPapers ?? 0}{" "}
          {t('sessionDetail.paperCountSuffix', { count: gradingState.totalPapers ?? 0 })}
        </span>
      </div>

      <div className="grading-progress-track">
        <div
          className={`grading-progress-fill ${gradingStatus === "completed" ? "done" : ""}`}
          style={{ width: `${progressPct}%` }}
        />
      </div>

      {gradingStatus === "running" && gradingState.currentPaperName && (
        <div className="grading-current">
          <Sparkles size={13} />
          {t('sessionDetail.processing')} <strong>{gradingState.currentPaperName}</strong>
        </div>
      )}

      <div className="grading-log">
        {gradingLogs.map((line, i) => (
          <div
            key={i}
            className={`grading-log-line ${line.startsWith(t('sessionDetail.completedLog').slice(0, 4)) ? "done" : ""}`}
          >
            {line}
          </div>
        ))}
        <div ref={logsEndRef} />
      </div>

      {gradingStatus === "running" && (
        <div className="grading-done-actions">
          <button className="btn btn-outline btn-sm" onClick={cancelGradingFlow}>
            {t('sessionDetail.cancelGrading')}
          </button>
        </div>
      )}

      {gradingStatus === "completed" && (
        <div className="grading-done-actions">
          <button
            className="btn btn-copper btn-sm"
            onClick={() => {
              setGradingState({});
              setGradingLogs([]);
            }}
          >
            <CheckCircle2 size={14} />
            {t('sessionDetail.viewResults')}
          </button>
        </div>
      )}
    </div>
  );

  // --- Tab: Analysis ---

  const renderAnalysis = () => {
    if (!hasGrades) {
      return (
        <div className="empty-state">
          <ClipboardList size={40} />
          <h3>{t('sessionDetail.emptyHeading')}</h3>
          <p>
            {tooFewPapers
              ? t('sessionDetail.emptyBodyTooFew')
              : t('sessionDetail.emptyBody')}
          </p>
          {tooFewPapers ? (
            <button
              className="btn btn-outline btn-sm"
              onClick={() => navigate(`/exams/${sessionId}/edit?tab=papers`)}
            >
              <Upload size={14} />
              {t('sessionDetail.uploadPapers')}
            </button>
          ) : (
            <button
              className="btn btn-copper btn-sm"
              onClick={startGradingFlow}
              disabled={detail.papers.length === 0}
            >
              <Play size={14} />
              {t('sessionDetail.startGradingButton')}
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="animate-fade-in">
        {analysisStatus === "loading" && (
          <p className="text-muted">{t('sessionDetail.analysisLoading')}</p>
        )}
        {analysisStatus === "error" && (
          <div
            style={{
              padding: "12px 16px",
              borderRadius: "var(--radius-md)",
              background: "var(--color-danger-bg)",
              color: "var(--color-danger)",
              fontSize: "var(--text-sm)",
            }}
          >
            {analysisError || t('sessionDetail.analysisError')}
          </div>
        )}

        {analysisStatus === "idle" && classAnalysis && (
          <>
            {/* Analysis header with stats */}
            <div className="analysis-header">
              <h3 className="analysis-header-title">{t('sessionDetail.analysisTitle')}</h3>
              <div className="analysis-header-stats">
                <span className="chip chip-grade">
                  {classAnalysis.paper_count} {t('sessionDetail.paperChipSuffix', { count: classAnalysis.paper_count })}
                </span>
                {classAverage && (
                  <span className="chip chip-grade">
                    {t('sessionDetail.classAvg')} {classAverage.average_score} /{" "}
                    {classAverage.max_score} ({classAverage.success_percent}%)
                  </span>
                )}
                <DifficultyIndicator difficulty={mapDifficultyLevel(classAnalysis.difficulty)} />
              </div>
            </div>

            {/* Strong / Weak topics */}
            <div className="analysis-grid">
              <div className="card card-padding">
                <div className="topics-panel-header">
                  <div className="flex items-center gap-8">
                    <TrendingUp
                      size={16}
                      style={{ color: "var(--color-success)" }}
                    />
                    <span className="topics-panel-title">{t('sessionDetail.strongTopics')}</span>
                  </div>
                  <span className="topics-panel-label">
                    {t('sessionDetail.topN', { n: Math.min(3, classAnalysis.analysis.successful_topics.length) })}
                  </span>
                </div>
                {classAnalysis.analysis.successful_topics.length === 0 ? (
                  <p className="text-muted text-sm">
                    {t('sessionDetail.noStrongTopics')}
                  </p>
                ) : (
                  classAnalysis.analysis.successful_topics
                    .slice(0, 3)
                    .map((t, i) => (
                      <div key={i} className="topic-row">
                        <span className="topic-row-bullet" />
                        <span className="topic-row-name">{t.topic}</span>
                        <span className="topic-row-pct text-success">
                          {t.avg_success_percent}%
                        </span>
                      </div>
                    ))
                )}
              </div>

              <div className="card card-padding">
                <div className="topics-panel-header">
                  <div className="flex items-center gap-8">
                    <TrendingDown
                      size={16}
                      style={{ color: "var(--color-danger)" }}
                    />
                    <span className="topics-panel-title">
                      {t('sessionDetail.weakTopics')}
                    </span>
                  </div>
                  <span className="topics-panel-label">
                    {t('sessionDetail.bottomN', { n: Math.min(3, classAnalysis.analysis.weak_topics.length) })}
                  </span>
                </div>
                {classAnalysis.analysis.weak_topics.length === 0 ? (
                  <p className="text-muted text-sm">
                    {t('sessionDetail.noWeakTopics')}
                  </p>
                ) : (
                  classAnalysis.analysis.weak_topics
                    .slice(0, 3)
                    .map((t, i) => (
                      <div key={i} className="topic-row">
                        <span className="topic-row-bullet" />
                        <span className="topic-row-name">{t.topic}</span>
                        <span className="topic-row-pct text-danger">
                          {t.avg_success_percent}%
                        </span>
                      </div>
                    ))
                )}
              </div>
            </div>

            {/* Recommendation */}
            <div className="recommendation-card">
              <div className="recommendation-icon">
                <Lightbulb />
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="recommendation-label">{t('sessionDetail.recommendation')}</div>
                <div className="recommendation-text">
                  {classAnalysis.analysis.class_recommendation ||
                    t('sessionDetail.noRecommendation')}
                </div>
              </div>
            </div>

            {/* Question performance */}
            <div className="card card-padding">
              <h3 className="card-title mb-16">{t('sessionDetail.questionPerformance')}</h3>
              <div>
                {classAnalysis.question_stats.map((q) => {
                  const success = Math.max(
                    0,
                    Math.min(100, q.success_percent)
                  );
                  return (
                    <div key={q.question_number} className="question-stat">
                      <div className="question-stat-number">
                        {t('sessionDetail.questionPrefix')}{q.question_number}
                      </div>
                      <div className="question-stat-text truncate">
                        {q.question_text}
                      </div>
                      <div className="question-stat-bar">
                        <div className="progress-bar">
                          <div
                            className={`progress-bar-fill ${progressColor(success)}`}
                            style={{ width: `${success}%` }}
                          />
                        </div>
                      </div>
                      <div className="question-stat-score">
                        {q.average_score}/{q.max_points}
                        <span
                          className="text-xs text-muted"
                          style={{ marginLeft: 6 }}
                        >
                          ({Math.round(success)}%)
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  // --- Tab: Students ---

  const renderStudents = () => {
    if (gradingMode === "mod_d") {
      return (
        <div className="sbs-tab-container">
          <ExamDSideBySide />
        </div>
      );
    }

    if (!hasGrades && studentRows.length === 0) {
      return (
        <div className="empty-state">
          <Users size={40} />
          <h3>{t('sessionDetail.noStudentResults')}</h3>
          <p>
            {tooFewPapers
              ? t('sessionDetail.noStudentResultsBodyTooFew')
              : t('sessionDetail.noStudentResultsBody')}
          </p>
          {tooFewPapers ? (
            <button
              className="btn btn-outline btn-sm"
              onClick={() => navigate(`/exams/${sessionId}/edit?tab=papers`)}
            >
              <Upload size={14} />
              {t('sessionDetail.uploadPapers')}
            </button>
          ) : (
            <button
              className="btn btn-copper btn-sm"
              onClick={startGradingFlow}
              disabled={detail.papers.length === 0}
            >
              <Play size={14} />
              {t('sessionDetail.startGradingButton')}
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="student-review-layout">
        {/* Student list sidebar */}
        <div className="student-list">
          <div className="student-list-header">
            {t('sessionDetail.sidebarHeader', { count: studentRows.length })}
          </div>
          <div className="student-list-items">
            {filteredStudents.map((item) => {
              const pct =
                item.score != null && item.maxScore
                  ? Math.round((item.score / item.maxScore) * 100)
                  : null;
              const scoreClass =
                pct === null
                  ? ""
                  : pct >= 90
                    ? "score-excellent"
                    : pct >= 75
                      ? "score-good"
                      : pct >= 60
                        ? "score-fair"
                        : "score-poor";
              return (
                <div
                  key={item.paper.id}
                  className={`student-list-item ${selectedRow?.paper.id === item.paper.id ? "active" : ""}`}
                  onClick={() => {
                    setSelectedPaperId(item.paper.id);
                    setRegradeSelected(new Set());
                  }}
                >
                  <span
                    className={`status-dot status-dot-${item.status === "graded" ? "completed" : item.status}`}
                  />
                  <div className="student-list-item-info">
                    <span className="student-list-item-name">
                      {item.paper.student_name}
                    </span>
                  </div>
                  <div className="student-list-item-right">
                    <span
                      className={`student-list-item-score ${scoreClass}`}
                    >
                      {pct !== null ? `${pct}%` : "—"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Student detail panel */}
        <div className="student-detail-panel">
          {!selectedRow && (
            <p className="text-muted">
              {t('sessionDetail.noPapersPlaceholder')}
            </p>
          )}

          {selectedRow && (
            <>
              <div className="student-detail-header">
                <div>
                  <div className="student-detail-name">
                    {selectedRow.paper.student_name}
                  </div>
                  <div className="text-sm text-muted">
                    <a
                      href={`/stream_file/${selectedRow.paper.file_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {selectedRow.paper.original_filename}
                    </a>
                  </div>
                </div>
                <div className="student-detail-score">
                  <ScoreRing
                    score={selectedRow.score ?? 0}
                    maxScore={selectedRow.maxScore ?? detail.max_points ?? 100}
                    size={50}
                  />
                  {selectedRow.score != null && (
                    <div className="student-detail-score-label">
                      <span className="student-detail-score-value">
                        {selectedRow.score}
                      </span>
                      <span className="student-detail-score-max">
                        / {selectedRow.maxScore ?? detail.max_points ?? 100}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Overall feedback */}
              {selectedRow.gradeEntry?.paper_grade && (
                <div className="mb-20">
                  <h4 className="text-sm font-semibold mb-8">
                    {t('sessionDetail.overallAssessment')}
                  </h4>
                  <textarea
                    className="form-textarea"
                    style={{ width: "100%" }}
                    value={overallFeedbackDraft}
                    onChange={(e) =>
                      setOverallFeedbackDraft(e.target.value)
                    }
                    onBlur={() => {
                      const next = overallFeedbackDraft.trim();
                      setGrades((prev) =>
                        prev.map((paper) => {
                          if (paper.paper_id !== selectedRow.paper.id)
                            return paper;
                          return {
                            ...paper,
                            paper_grade: paper.paper_grade
                              ? {
                                  ...paper.paper_grade,
                                  overall_feedback: next,
                                }
                              : paper.paper_grade,
                          };
                        })
                      );
                    }}
                    rows={4}
                    placeholder={t('sessionDetail.editFeedback')}
                  />
                </div>
              )}

              {/* Question breakdown */}
              {selectedRow.gradeEntry && (
                <>
                  {selectedRow.gradeEntry?.paper_grade?.has_low_confidence && (
                    <div style={{
                      padding: "10px 14px",
                      background: "#fef3c7",
                      border: "1px solid #f59e0b",
                      borderRadius: 6,
                      marginBottom: 12,
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      fontSize: 13,
                    }}>
                      <span style={{ color: "#d97706", fontSize: 16 }}>⚠</span>
                      <span>
                        <strong>
                          {t('sessionDetail.lowConfidenceBanner', { count: selectedRow.gradeEntry.question_grades.filter(q => q.question_number_matched === false).length })}
                        </strong>
                      </span>
                    </div>
                  )}
                  <h4 className="text-sm font-semibold mb-12">
                    {t('sessionDetail.questionDetails')}
                  </h4>
                  <div>
                    {selectedRow.gradeEntry.question_grades.map((q) => {
                      const isEditing =
                        editingCell?.questionNumber === q.question_number &&
                        editingCell?.field === "feedback";
                      const display = q.question_display || String(q.question_number);
                      const isRegradeSelected = regradeSelected.has(display);
                      const isRegradeLoading = regradeLoading && isRegradeSelected;
                      return (
                        <div
                          key={q.id}
                          className={[
                            "question-stat",
                            isRegradeSelected ? "regrade-selected" : "",
                            isRegradeLoading ? "regrade-loading" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                        >
                          <input
                            type="checkbox"
                            className="question-stat-checkbox"
                            checked={isRegradeSelected}
                            disabled={regradeLoading}
                            onChange={(e) => {
                              setRegradeSelected((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(display);
                                else next.delete(display);
                                return next;
                              });
                            }}
                          />
                          <div
                            className="question-stat-number"
                            style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}
                            title={t('common.clickToViewRaw')}
                            onClick={(e) => {
                              e.stopPropagation();
                              setRawJsonModal({ data: q, title: t('sessionDetail.rawDataTitle', { n: q.question_display || q.question_number }) });
                            }}
                          >
                            {t('sessionDetail.questionPrefix')}{q.question_display || q.question_number}
                            {q.question_number_matched === false && (
                              <span
                                title={t('sessionDetail.mismatchWarning')}
                                style={{ color: "#f59e0b", fontSize: 14 }}
                              >⚠</span>
                            )}
                            <span style={{ color: "var(--text-muted)", fontSize: 11, fontWeight: 400 }}>{"{}"}</span>
                          </div>
                          {isEditing ? (
                            <div
                              className="editable-cell-editing"
                              style={{
                                flex: 1,
                                display: "flex",
                                gap: 8,
                                alignItems: "center",
                              }}
                            >
                              <textarea
                                value={editValue}
                                onChange={(e) =>
                                  setEditValue(e.target.value)
                                }
                                style={{ flex: 1, minHeight: 60 }}
                                autoFocus
                              />
                              <button
                                className="btn btn-icon btn-xs"
                                onClick={saveEdit}
                              >
                                <Check size={12} />
                              </button>
                              <button
                                className="btn btn-icon btn-xs"
                                onClick={cancelEdit}
                              >
                                <X size={12} />
                              </button>
                            </div>
                          ) : (
                            <div
                              className="question-stat-text editable-cell"
                              onClick={() =>
                                startEdit(
                                  q.question_number,
                                  "feedback",
                                  q.feedback
                                )
                              }
                              title={t('common.clickToEdit')}
                              style={{ flex: 1 }}
                            >
                              {q.feedback || t('common.noFeedback')}
                            </div>
                          )}
                          <div className="question-stat-score">
                            {q.score}/{q.max_score}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {regradeSelected.size > 0 && (
                    <div className="regrade-action-bar">
                      <span className="regrade-action-bar-label">
                        {t('sessionDetail.nQuestionsSelected', { count: regradeSelected.size })}
                      </span>
                      <button
                        className="btn-regrade"
                        disabled={regradeLoading}
                        onClick={handleRegrade}
                      >
                        {regradeLoading && <span className="regrade-spinner" />}
                        {t('sessionDetail.regrade')}
                      </button>
                    </div>
                  )}
                </>
              )}

              {/* Pending state */}
              {selectedRow.status === "pending" &&
                !selectedRow.gradeEntry && (
                  <div
                    style={{
                      padding: 24,
                      textAlign: "center",
                      color: "var(--text-muted)",
                    }}
                  >
                    <p>
                      {t('sessionDetail.pendingState')}
                    </p>
                  </div>
                )}
            </>
          )}
        </div>
      </div>
    );
  };

  // --- Main render ---

  return (
    <div className="animate-fade-in">
      {/* Exam detail top section */}
      <div className="exam-detail-top">
        <div className="exam-detail-info">
          <button
            className={hasRubric ? "chip chip-check" : "chip chip-papers"}
            onClick={() => navigate(editRubricPath)}
          >
            {hasRubric ? <CheckCircle2 size={14} /> : <X size={14} />}
            {hasRubric ? t('sessionDetail.rubricLoaded') : t('sessionDetail.noRubric')}
          </button>
          {gradingMode !== "mod_c" && (
          <button
            className={detail.questions.length === 0 ? "chip chip-papers" : questionsApproved ? "chip chip-check" : "chip chip-papers"}
            onClick={() => navigate(editQuestionsPath)}
            style={detail.questions.length > 0 && !questionsApproved ? { background: "#f59e0b", color: "#fff", border: "none" } : undefined}
          >
            {detail.questions.length > 0 ? <CheckCircle2 size={14} /> : <X size={14} />}
            {detail.questions.length > 0
              ? questionsApproved
                ? t('sessionDetail.nQuestionsChip', { count: detail.questions.length })
                : t('sessionDetail.questionsAwaitingApproval', { count: detail.questions.length })
              : t('sessionDetail.questionsNotLoaded')}
          </button>
          )}
          <button
            className="chip chip-papers"
            onClick={() => navigate(editPapersPath)}
          >
            <Copy size={14} />
            {t('sessionDetail.nPapersChip', { count: detail.papers.length })}
          </button>
          <button
            className="btn btn-outline btn-sm"
            onClick={() => navigate(setupPath)}
          >
            <Settings size={14} />
            {t('sessionDetail.editButton')}
          </button>
        </div>
        <div className="exam-detail-actions">
          {!tooFewPapers && (
            <button
              className="btn btn-copper btn-sm"
              onClick={startGradingFlow}
              disabled={
                gradingStatus === "running" || detail.papers.length === 0
              }
            >
              {gradingStatus === "running" ? (
                <Loader2 size={14} className="spin" />
              ) : (
                <Play size={14} />
              )}
              {gradingStatus === "running"
                ? t('sessionDetail.gradingButton')
                : t('sessionDetail.startGradingButton')}
            </button>
          )}
        </div>
      </div>

      {/* Tabs — only show if there are grading results */}
      {classAnalysis && (
        <div className="tabs">
          <button
            className={`tab ${activeTab === "analysis" ? "active" : ""}`}
            onClick={() => setActiveTab("analysis")}
          >
            {t('sessionDetail.tabAnalysis')}
          </button>
          <button
            className={`tab ${activeTab === "students" ? "active" : ""}`}
            onClick={() => setActiveTab("students")}
          >
            {t('sessionDetail.tabStudents')}
          </button>
          <button
            className={`tab ${activeTab === "export" ? "active" : ""}`}
            onClick={() => setActiveTab("export")}
          >
            {t('sessionDetail.tabExport')}
          </button>
        </div>
      )}

      {/* Grading overlay — shown across all tabs while grading */}
      {(gradingStatus === "running" || gradingStatus === "completed") &&
        gradingState.gradingId &&
        renderGradingOverlay()}

      {/* Tab content — only when not actively grading */}
      {gradingStatus === "idle" && activeTab === "analysis" && renderAnalysis()}
      {gradingStatus === "idle" && activeTab === "students" && renderStudents()}
      {gradingStatus === "idle" && activeTab === "export" && (
        <div className="animate-fade-in">
          <div className="coming-soon">
            <Download size={64} />
            <h3>{t('sessionDetail.exportComingSoon')}</h3>
            <p className="text-muted">
              {t('sessionDetail.exportComingSoonBody')}
            </p>
          </div>
        </div>
      )}

      {/* Raw JSON modal (mod_d) */}
      {rawJsonModal && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 1000,
            background: "rgba(0,0,0,0.55)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
          onClick={() => setRawJsonModal(null)}
        >
          <div
            style={{
              background: "#ffffff",
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              padding: 20,
              maxWidth: 640,
              width: "90%",
              maxHeight: "80vh",
              display: "flex",
              flexDirection: "column",
              gap: 12,
              boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontWeight: 600, fontSize: 14, color: "#111827" }}>{rawJsonModal.title}</span>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setRawJsonModal(null)}
              >
                <X size={14} />
              </button>
            </div>
            <pre
              style={{
                overflowY: "auto",
                fontSize: 12,
                lineHeight: 1.6,
                padding: "12px 14px",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 6,
                margin: 0,
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
                color: "#1e293b",
              }}
            >
              {JSON.stringify(rawJsonModal.data, null, 2)}
            </pre>
          </div>
        </div>
      )}

      {/* Failed grading banner */}
      {gradingStatus === "failed" && (
        <div
          style={{
            padding: "12px 16px",
            borderRadius: "var(--radius-md)",
            background: "var(--color-danger-bg)",
            color: "var(--color-danger)",
            fontSize: "var(--text-sm)",
            marginTop: 16,
          }}
        >
          {t('sessionDetail.gradingFailedBanner')}
        </div>
      )}
    </div>
  );
};

export default SessionDetailPage;
